import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { serviceProviderApi, organizationApi, openPdfExport } from '../services/api';
import {
  ServiceProvider,
  ServiceProviderRole,
  AvvStatus,
  Organization
} from '@dsgvo-buddy/types';
import {
  Title,
  Button,
  Table,
  TableColumn,
  TableRow,
  TableCell,
  Input,
  Select,
  Option,
  FlexBox,
  Text,
  Icon,
  ObjectStatus,
  BusyIndicator,
  Card,
  CardHeader
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import PageIntro from '../components/PageIntro';
import { pageIntros } from '../helpers/helpTexts';

const roleConfig: Record<ServiceProviderRole, { label: string; state: string }> = {
  [ServiceProviderRole.PROCESSOR]: { label: 'Auftragsverarbeiter', state: 'Information' },
  [ServiceProviderRole.JOINT_CONTROLLER]: { label: 'Gem. Verantwortlicher', state: 'Warning' },
  [ServiceProviderRole.RECIPIENT]: { label: 'Empfänger', state: 'None' }
};

const avvStatusConfig: Record<AvvStatus, { label: string; state: string }> = {
  [AvvStatus.DRAFT]: { label: 'Entwurf', state: 'None' },
  [AvvStatus.ACTIVE]: { label: 'Aktiv', state: 'Success' },
  [AvvStatus.EXPIRED]: { label: 'Abgelaufen', state: 'Error' },
  [AvvStatus.TERMINATED]: { label: 'Beendet', state: 'None' }
};

function getActiveAvvStatus(provider: ServiceProvider): { label: string; state: string } | null {
  if (!provider.avv_contracts || provider.avv_contracts.length === 0) return null;

  // Prefer active contract; otherwise pick the most recently updated one
  const active = provider.avv_contracts.find(c => c.status === AvvStatus.ACTIVE);
  if (active) return avvStatusConfig[AvvStatus.ACTIVE];

  const sorted = [...provider.avv_contracts].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );
  return avvStatusConfig[sorted[0].status];
}

export default function ServiceProviderListPage() {
  const navigate = useNavigate();
  const { canCreate } = useUserRole();
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<ServiceProvider[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<ServiceProviderRole | ''>('');

  useEffect(() => { loadOrganizations(); }, []);
  useEffect(() => { if (currentOrg) { loadProviders(); } }, [currentOrg]);
  useEffect(() => { applyFilters(); }, [providers, searchTerm, roleFilter]);

  const loadOrganizations = async () => {
    const response = await organizationApi.getAll();
    if (response.success && response.data) {
      if (response.data.length > 0) setCurrentOrg(response.data[0]);
    }
    setLoading(false);
  };

  const loadProviders = async () => {
    if (!currentOrg) return;
    setLoading(true);
    const response = await serviceProviderApi.getByOrganization(currentOrg.id);
    if (response.success && response.data) setProviders(response.data);
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...providers];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(term) ||
        p.service_description?.toLowerCase().includes(term) ||
        p.hosting_location?.toLowerCase().includes(term)
      );
    }
    if (roleFilter) filtered = filtered.filter(p => p.role === roleFilter);
    setFilteredProviders(filtered);
  };

  if (loading && !currentOrg) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  return (
    <div className="page-layout">
      {/* Page Header */}
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>Dienstleister</Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
            Auftragsverarbeiter, gemeinsame Verantwortliche und Empfänger
          </Text>
        </div>
        <FlexBox style={{ gap: '0.5rem' }}>
          <Button icon="pdf-attachment" design="Default" onClick={() => currentOrg && openPdfExport('service-providers', currentOrg.id)}>PDF Export</Button>
          {canCreate && (
            <Button icon="add" design="Emphasized" onClick={() => navigate('/service-providers/new')}>
              Neuer Dienstleister
            </Button>
          )}
        </FlexBox>
      </FlexBox>

      <PageIntro {...pageIntros['service-providers']} storageKey="service-providers" />

      {/* Filter Bar */}
      <FlexBox
        alignItems="End"
        wrap="Wrap"
        style={{
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '1rem',
          background: 'var(--sapObjectHeader_Background, var(--sapBaseColor))',
          borderRadius: '0.5rem',
          border: '1px solid var(--sapGroup_TitleBorderColor, #d9d9d9)'
        }}
      >
        <div style={{ flex: '1 1 300px', minWidth: '200px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Suche</Text>
          <Input
            placeholder="Name, Beschreibung, Standort..."
            icon={<Icon name="search" />}
            value={searchTerm}
            onInput={(e: any) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ flex: '0 0 220px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Rolle</Text>
          <Select onChange={(e: any) => setRoleFilter(e.detail.selectedOption.value)} style={{ width: '100%' }}>
            <Option value="">Alle Rollen</Option>
            <Option value={ServiceProviderRole.PROCESSOR}>Auftragsverarbeiter</Option>
            <Option value={ServiceProviderRole.JOINT_CONTROLLER}>Gemeinsamer Verantwortlicher</Option>
            <Option value={ServiceProviderRole.RECIPIENT}>Normaler Empfänger</Option>
          </Select>
        </div>

        {(searchTerm || roleFilter) && (
          <Button
            icon="decline"
            design="Transparent"
            onClick={() => { setSearchTerm(''); setRoleFilter(''); }}
            tooltip="Filter zurücksetzen"
          />
        )}
      </FlexBox>

      {/* Table Card */}
      <Card style={{ overflow: 'hidden' }}
        header={
          <CardHeader
            titleText={`${filteredProviders.length} von ${providers.length} Dienstleistern`}
            subtitleText="Klicken Sie auf eine Zeile für Details"
            avatar={<Icon name="supplier" />}
          />
        }
      >
        {loading ? (
          <FlexBox alignItems="Center" justifyContent="Center" style={{ padding: '3rem' }}>
            <BusyIndicator active size="Large" />
          </FlexBox>
        ) : filteredProviders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Icon name="supplier" style={{ fontSize: '3rem', color: 'var(--sapNeutralColor)', display: 'block', marginBottom: '1rem' }} />
            <Title level="H5" style={{ marginBottom: '0.5rem' }}>
              {providers.length === 0 ? 'Noch keine Dienstleister' : 'Keine Treffer'}
            </Title>
            <Text style={{ display: 'block', color: 'var(--sapNeutralTextColor)', marginBottom: '1rem' }}>
              {providers.length === 0
                ? 'Erfassen Sie Ihren ersten Dienstleister.'
                : 'Passen Sie Ihre Filterkriterien an.'}
            </Text>
            {providers.length === 0 && canCreate && (
              <Button icon="add" design="Emphasized" onClick={() => navigate('/service-providers/new')}>
                Neuer Dienstleister erstellen
              </Button>
            )}
          </div>
        ) : (
          <div style={{ padding: '0 1rem 1rem' }}>
            <Table
              style={{ width: '100%' }}
              onRowClick={(e: any) => {
                const index = parseInt(e.detail.row.dataset.index);
                navigate(`/service-providers/${filteredProviders[index].id}`);
              }}
              columns={
                <>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Name</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Rolle</Text></TableColumn>
                  <TableColumn demandPopin minWidth={500}><Text style={{ fontWeight: '600' }}>Standort</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>AVV-Status</Text></TableColumn>
                  <TableColumn demandPopin minWidth={600}><Text style={{ fontWeight: '600' }}>Aktualisiert</Text></TableColumn>
                </>
              }
            >
              {filteredProviders.map((provider, index) => (
                <TableRow key={provider.id} data-index={index} type="Active">
                  <TableCell>
                    <div>
                      <Text style={{ fontWeight: '600' }}>{provider.name}</Text>
                      {provider.service_description && (
                        <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginTop: '0.125rem' }}>
                          {provider.service_description.length > 70
                            ? provider.service_description.substring(0, 70) + '...'
                            : provider.service_description}
                        </Text>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <ObjectStatus state={roleConfig[provider.role].state as any} showDefaultIcon>
                      {roleConfig[provider.role].label}
                    </ObjectStatus>
                  </TableCell>
                  <TableCell>
                    <Text>{provider.hosting_location ?? '-'}</Text>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const avv = getActiveAvvStatus(provider);
                      if (!avv) {
                        // Only processors require an AVV; warn if one is missing for them
                        return provider.role === ServiceProviderRole.PROCESSOR ? (
                          <ObjectStatus state="Warning" showDefaultIcon>
                            Kein AVV
                          </ObjectStatus>
                        ) : (
                          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>-</Text>
                        );
                      }
                      return (
                        <ObjectStatus state={avv.state as any} showDefaultIcon>
                          {avv.label}
                        </ObjectStatus>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Text>{new Date(provider.updated_at).toLocaleDateString('de-DE')}</Text>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
